<template>
  <div
    class="manager-detail-modern"
    role="main"
    aria-label="Détail du membre du personnel logistique"
  >
    <!-- Modern Skeleton Loading State -->
    <div
      v-if="isLoading"
      class="skeleton-loading"
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <!-- Skeleton Page Header -->
      <div class="skeleton-header mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="skeleton-title mb-2" />
            <div class="skeleton-subtitle" />
          </div>
          <div class="skeleton-actions">
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn" />
          </div>
        </div>
      </div>

      <!-- Skeleton Profile Layout -->
      <b-row class="mb-4">
        <b-col lg="4">
          <!-- Skeleton Profile Card -->
          <div class="skeleton-profile-card">
            <div class="skeleton-profile-header">
              <div class="skeleton-avatar" />
              <div class="skeleton-name" />
              <div class="skeleton-role" />
              <div class="skeleton-status" />
            </div>
            <div class="skeleton-profile-stats">
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Skeleton Information Cards -->
          <div class="skeleton-info-grid">
            <!-- Contact Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Info Card -->
            <div class="skeleton-info-card skeleton-info-card-full">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value skeleton-info-value-long" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row
        v-if="staff.can_receive_commission"
        class="mb-4"
      >
        <b-col>
          <b-card>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                <feather-icon
                  icon="PercentIcon"
                  size="18"
                  class="mr-1"
                />
                Commissions du commercial
              </h4>
              <b-button
                variant="primary"
                size="sm"
                @click="openCommissionModal"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-1"
                />
                Ajouter une période
              </b-button>
            </div>

            <div
              v-if="loadingCommissions"
              class="text-center py-4"
            >
              <b-spinner variant="primary" />
            </div>

            <div
              v-else
              class="premium-table-wrapper"
            >
              <b-table
                :items="commissionRates"
                :fields="commissionFields"
                responsive
                hover
                small
              >
                <template #cell(rate)="data">
                  <span class="font-weight-bold text-success">
                    {{ formatAmount(data.item.rate) }} DT
                  </span>
                </template>

                <template #cell(period)="data">
                  {{ formatShortDate(data.item.start_date) }} - {{ formatShortDate(data.item.end_date) }}
                </template>

                <template #cell(status)="data">
                  <b-badge :variant="getCommissionStatusVariant(data.item)">
                    {{ getCommissionStatusLabel(data.item) }}
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    v-if="canDeleteCommission(data.item)"
                    variant="outline-danger"
                    size="sm"
                    @click="confirmDeleteCommission(data.item)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                    />
                  </b-button>
                </template>
              </b-table>
            </div>

            <div
              v-if="!loadingCommissions && commissionRates.length === 0"
              class="text-center text-muted py-3"
            >
              Aucune période de commission définie.
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="error-container"
      aria-live="assertive"
      role="alert"
    >
      <div class="error-content">
        <div class="error-icon">
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
          />
        </div>
        <h3 class="error-title">
          Erreur
        </h3>
        <p class="error-message">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Content loaded successfully -->
    <div v-else>
      <!-- Modern Page Header -->
      <div class="page-header mb-4">
        <!-- Accessible page title and description for screen readers -->
        <h2
          id="staff-profile-title"
          class="sr-only"
        >
          Profil du membre du personnel logistique
        </h2>
        <p
          id="staff-profile-desc"
          class="sr-only"
        >
          Informations détaillées et gestion du membre du personnel logistique.
        </p>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h2 class="page-title mb-1">
              Profil du Personnel
            </h2>
            <p class="page-subtitle text-muted mb-0">
              Informations détaillées et gestion du membre du personnel
            </p>
          </div>
          <div class="header-actions">
            <b-button
              variant="warning"
              size="lg"
              class="btn-modern mr-2"
              :to="{ name: 'staff-edit', params: { id: staff.id }}"
            >
              <feather-icon
                icon="EditIcon"
                size="16"
                class="mr-2"
              />
              Modifier
            </b-button>
            <b-button
              v-if="isAdmin"
              variant="info"
              size="lg"
              class="btn-modern mr-2"
              :aria-label="`Changer le mot de passe de ${staff.username}`"
              @click="openPasswordModal"
            >
              <feather-icon
                icon="KeyIcon"
                size="16"
                class="mr-2"
                aria-hidden="true"
              />
              Mot de passe
            </b-button>
            <b-button
              :variant="staff.is_active ? 'danger' : 'success'"
              size="lg"
              class="btn-modern"
              @click="toggleStaffStatus"
            >
              <feather-icon
                :icon="staff.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
                size="16"
                class="mr-2"
              />
              {{ staff.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Modern Profile Card -->
      <b-row class="mb-4">
        <b-col lg="4">
          <div class="profile-card-modern">
            <div class="profile-header">
              <div class="profile-avatar">
                <div class="avatar-circle-large">
                  <span class="avatar-initials-large">{{ getInitials(staff.username) }}</span>
                </div>
                <div
                  class="status-indicator-large"
                  :class="{ active: staff.is_active }"
                />
              </div>
              <h3 class="profile-name">
                {{ staff.username }}
              </h3>
              <p class="profile-role">
                {{ formatRole(staff.role) || 'Personnel' }}
              </p>
              <div class="profile-status">
                <span
                  class="status-badge-large"
                  :class="staff.is_active ? 'active' : 'inactive'"
                >
                  {{ staff.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </div>
            </div>

            <div class="profile-stats">
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="ClockIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Créé le</span>
                  <span class="stat-value">{{ formatDate(staff.created_at) }}</span>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Modifié le</span>
                  <span class="stat-value">{{ formatDate(staff.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Information Cards Grid -->
          <div class="info-cards-grid">
            <!-- Contact Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon contact">
                  <feather-icon
                    icon="MailIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations de Contact
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="MailIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Email</span>
                    <span class="info-value">{{ staff.email || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="PhoneIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Téléphone</span>
                    <span class="info-value">{{ staff.phone || 'Non défini' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon work">
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations Professionnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Entrepôt Assigné</span>
                    <span class="info-value">{{ staff.repository && staff.repository.name ? staff.repository.name : 'Non assigné' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Rôle</span>
                    <span class="info-value">{{ formatRole(staff.role) || 'Personnel' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon personal">
                  <feather-icon
                    icon="UserIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations Personnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">CIN</span>
                    <span class="info-value">{{ staff.cin || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Adresse</span>
                    <span class="info-value">{{ staff.address || 'Non définie' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Account Security & Audit Card -->
      <b-row>
        <b-col lg="12">
          <b-card
            title="Sécurité du compte & Audit"
            class="mb-4 shadow-sm"
            aria-label="Sécurité du compte et historique des modifications"
          >
            <div
              class="d-flex justify-content-between align-items-center flex-wrap"
              style="gap: 1rem;"
            >
              <div
                v-if="staff.last_password_change"
                class="d-flex align-items-center"
              >
                <div class="avatar bg-light-primary p-2 mr-2">
                  <feather-icon
                    icon="LockIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <div>
                  <strong class="d-block text-dark font-weight-bold">
                    Dernière modification du mot de passe
                  </strong>
                  <span class="small text-muted">
                    Modifié le {{ formatDateTime(staff.last_password_change.created_at) }} par <strong>{{ staff.last_password_change.changed_by_display }}</strong>
                  </span>
                </div>
              </div>
              <div
                v-else
                class="text-muted small d-flex align-items-center"
              >
                <feather-icon
                  icon="ShieldIcon"
                  class="mr-50 text-secondary"
                  size="18"
                />
                <span>Aucune modification de mot de passe enregistrée pour ce compte.</span>
              </div>

              <b-button
                variant="outline-primary"
                size="sm"
                @click="openPasswordModal"
              >
                <feather-icon
                  icon="LockIcon"
                  size="14"
                  class="mr-50"
                />
                <span>Changer le mot de passe</span>
              </b-button>
            </div>

            <!-- Collapsible Audit History List -->
            <div
              v-if="staff.password_change_history && staff.password_change_history.length > 0"
              class="mt-3 pt-2 border-top"
            >
              <b-button
                v-b-toggle.staff-password-history-collapse
                variant="flat-primary"
                size="sm"
                class="p-0 font-weight-bold"
              >
                <feather-icon
                  icon="HistoryIcon"
                  size="14"
                  class="mr-25"
                />
                Voir l'historique complet ({{ staff.password_change_history.length }})
              </b-button>

              <b-collapse
                id="staff-password-history-collapse"
                class="mt-2"
              >
                <b-list-group
                  flush
                  class="border rounded"
                >
                  <b-list-group-item
                    v-for="log in staff.password_change_history"
                    :key="log.id"
                    class="py-50 px-1 d-flex justify-content-between align-items-center small"
                  >
                    <span>
                      <feather-icon
                        icon="UserCheckIcon"
                        size="12"
                        class="mr-50 text-primary"
                      />
                      <strong>{{ log.changed_by_display }}</strong>
                    </span>
                    <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
                  </b-list-group-item>
                </b-list-group>
              </b-collapse>
            </div>

            <!-- Account audit metadata (create / update timestamps) -->
            <div class="mt-3 pt-2 border-top">
              <b-list-group
                flush
                class="mb-0"
              >
                <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                  <span>
                    <feather-icon
                      icon="PlusCircleIcon"
                      size="14"
                      class="mr-50 text-success"
                    />
                    <span class="text-muted">Compte créé le</span>
                  </span>
                  <span class="text-muted">{{ formatDateTime(staff.created_at) }}</span>
                </b-list-group-item>
                <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                  <span>
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="14"
                      class="mr-50 text-primary"
                    />
                    <span class="text-muted">Dernière mise à jour</span>
                  </span>
                  <span class="text-muted">{{ formatDateTime(staff.updated_at) }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Commission Section for Sales (Synced from MZ) -->
      <b-row
        v-if="staff.can_receive_commission"
        class="mb-4"
      >
        <b-col>
          <b-card>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                <feather-icon
                  icon="PercentIcon"
                  size="18"
                  class="mr-1"
                />
                Commissions du commercial
              </h4>
              <b-button
                variant="primary"
                size="sm"
                @click="openCommissionModal"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-1"
                />
                Ajouter une période
              </b-button>
            </div>

            <div
              v-if="loadingCommissions"
              class="text-center py-4"
            >
              <b-spinner variant="primary" />
            </div>

            <div
              v-else
              class="premium-table-wrapper"
            >
              <b-table
                :items="commissionRates"
                :fields="commissionFields"
                responsive
                hover
                small
              >
                <template #cell(rate)="data">
                  <span class="font-weight-bold text-success">
                    {{ formatAmount(data.item.rate) }} DT
                  </span>
                </template>

                <template #cell(period)="data">
                  {{ formatShortDate(data.item.start_date) }} - {{ formatShortDate(data.item.end_date) }}
                </template>

                <template #cell(status)="data">
                  <b-badge :variant="getCommissionStatusVariant(data.item)">
                    {{ getCommissionStatusLabel(data.item) }}
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    v-if="canDeleteCommission(data.item)"
                    variant="outline-danger"
                    size="sm"
                    @click="confirmDeleteCommission(data.item)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                    />
                  </b-button>
                </template>
              </b-table>
            </div>

            <div
              v-if="!loadingCommissions && commissionRates.length === 0"
              class="text-center text-muted py-3"
            >
              Aucune période de commission définie.
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Modal for Changing Staff Password -->
    <b-modal
      id="staff-password-modal"
      v-model="showPasswordModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordForm"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div class="avatar bg-light-primary p-2 mr-2">
              <feather-icon
                icon="LockIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0">
                Changer le mot de passe
              </h5>
              <small
                v-if="staff"
                class="text-muted"
              >
                {{ staff.username }} • {{ staff.email || 'Pas d\'email' }}
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            @click="close"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div v-if="staff">
        <!-- Password Audit Log Banner (only shown if a modification exists) -->
        <b-alert
          v-if="staff.last_password_change"
          show
          variant="light-primary"
          class="mb-3 border-left-primary shadow-sm"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="mr-2 text-primary"
            />
            <div>
              <strong class="d-block text-dark font-weight-bold">
                Dernière modification du mot de passe
              </strong>
              <span class="small text-muted">
                Modifié le {{ formatDateTime(staff.last_password_change.created_at) }} par <strong>{{ staff.last_password_change.changed_by_display }}</strong>
              </span>
            </div>
          </div>
        </b-alert>

        <!-- Collapsible Audit History -->
        <div
          v-if="staff.password_change_history && staff.password_change_history.length > 0"
          class="mb-3"
        >
          <b-button
            v-b-toggle.password-history-collapse
            variant="flat-primary"
            size="sm"
            class="p-0 font-weight-bold"
          >
            <feather-icon
              icon="HistoryIcon"
              size="14"
              class="mr-25"
            />
            Historique des modifications ({{ staff.password_change_history.length }})
          </b-button>

          <b-collapse
            id="password-history-collapse"
            class="mt-2"
          >
            <b-list-group
              flush
              class="border rounded"
            >
              <b-list-group-item
                v-for="log in staff.password_change_history"
                :key="log.id"
                class="py-50 px-1 d-flex justify-content-between align-items-center small"
              >
                <span>
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                    class="mr-50 text-primary"
                  />
                  <strong>{{ log.changed_by_display }}</strong>
                </span>
                <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-collapse>
        </div>

        <b-form @submit.prevent="savePasswordChange">
          <!-- New Password -->
          <b-form-group
            label-class="form-label-premium"
            label="Nouveau mot de passe *"
            label-for="modal-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="modal-password"
                v-model="passwordForm.password"
                class="premium-input"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Entrez le nouveau mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <!-- Password Strength Meter -->
            <div
              v-if="passwordForm.password"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-50">
                <small class="text-muted">Force du mot de passe:</small>
                <small
                  :class="'text-' + passwordStrengthClass"
                  class="font-weight-bold"
                >
                  {{ passwordStrengthText }}
                </small>
              </div>
              <b-progress
                :value="passwordStrength"
                :variant="passwordStrengthClass"
                height="6px"
              />
            </div>
          </b-form-group>

          <!-- Confirm Password -->
          <b-form-group
            label-class="form-label-premium"
            label="Confirmer le mot de passe *"
            label-for="modal-confirm-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="modal-confirm-password"
                v-model="passwordForm.confirm_password"
                class="premium-input"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirmez le mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <small
              v-if="passwordForm.confirm_password && !passwordsMatch"
              class="text-danger mt-50 d-block"
            >
              Les mots de passe ne correspondent pas.
            </small>
          </b-form-group>

          <!-- Generate Password Button -->
          <!-- Generate Password Button -->
          <div class="mb-4">
            <PasswordGenerator
              :user="staff"
              user-type="staff"
              button-text="Générer un mot de passe sécurisé"
              @generated="onPasswordGenerated"
            />
          </div>

          <!-- Action Footer -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="showPasswordModal = false"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              :disabled="isPasswordSubmitting || !isPasswordFormValid"
              type="submit"
            >
              <b-spinner
                v-if="isPasswordSubmitting"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-50"
              />
              Enregistrer le mot de passe
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>

    <!-- Status Confirmation Modal -->
    <b-modal
      ref="statusModal"
      :title="statusModalTitle"
      size="md"
      centered
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
    >
      <p class="text-center mb-3">
        {{ statusModalMessage }}
      </p>
      <div class="text-center">
        <feather-icon
          :icon="staff.is_active ? 'AlertTriangleIcon' : 'CheckCircleIcon'"
          size="48"
          :class="staff.is_active ? 'text-warning' : 'text-success'"
        />
      </div>

      <template #modal-ok>
        <feather-icon
          :icon="staff.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
          size="16"
          class="mr-1"
        />
        {{ staff.is_active ? 'Désactiver' : 'Activer' }}
      </template>

      <template #modal-cancel>
        <feather-icon
          icon="XIcon"
          size="16"
          class="mr-1"
        />
        Annuler
      </template>
    </b-modal>

    <!-- Commission Modal (Synced from MZ) -->
    <b-modal
      v-model="showCommissionModal"
      title="Ajouter une période de commission"
      hide-footer
    >
      <b-form @submit.prevent="createCommissionRate">
        <b-form-group
          label-class="form-label-premium"
          label="Commission par commande (DT) *"
        >
          <b-form-input
            v-model="commissionForm.rate"
            class="premium-input"
            type="number"
            step="0.001"
            min="0"
            placeholder="0.200"
            required
          />
        </b-form-group>
        <b-row>
          <b-col>
            <b-form-group
              label-class="form-label-premium"
              label="Date de début *"
            >
              <b-form-input
                v-model="commissionForm.start_date"
                class="premium-input"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label-class="form-label-premium"
              label="Date de fin *"
            >
              <b-form-input
                v-model="commissionForm.end_date"
                class="premium-input"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end gap-2">
          <b-button
            variant="outline-secondary"
            @click="showCommissionModal = false"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="creatingCommission"
          >
            {{ creatingCommission ? 'Création...' : 'Créer' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Commission Delete Modal (Synced from MZ) -->
    <b-modal
      v-model="showCommissionDeleteModal"
      title="Supprimer la période"
      centered
      ok-variant="danger"
      cancel-variant="outline-secondary"
      @ok="handleCommissionDelete"
    >
      <p class="mb-2">
        Confirmer la suppression de cette période de commission ?
      </p>
      <p class="text-danger mb-2">
        Cette action est irréversible.
      </p>
      <div v-if="commissionToDelete">
        <div class="font-weight-bold">
          {{ formatShortDate(commissionToDelete.start_date) }} - {{ formatShortDate(commissionToDelete.end_date) }}
        </div>
        <div class="text-muted">
          {{ formatAmount(commissionToDelete.rate) }} DT / commande
        </div>
      </div>
      <template #modal-ok>
        <feather-icon
          icon="TrashIcon"
          size="14"
          class="mr-1"
        />
        Supprimer
      </template>
      <template #modal-cancel>
        <feather-icon
          icon="XIcon"
          size="14"
          class="mr-1"
        />
        Annuler
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import storeAuth from '@/store/store'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  BButton,
  BRow,
  BCol,
  BModal,
  BCard,
  BTable,
  BSpinner,
  BBadge,
  BForm,
  BFormGroup,
  BFormInput,
  BListGroup,
  BListGroupItem,
  BCollapse,
  BInputGroup,
  BInputGroupAppend,
  BProgress,
  VBToggle,
} from 'bootstrap-vue'

export default {
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    PasswordGenerator,
    BButton,
    BRow,
    BCol,
    BModal,
    BCard,
    BTable,
    BSpinner,
    BBadge,
    BForm,
    BFormGroup,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BCollapse,
    BInputGroup,
    BInputGroupAppend,
    BProgress,
  },
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      staff: {},
      error: null,
      statusModalTitle: '',
      statusModalMessage: '',
      loadingCommissions: false,
      creatingCommission: false,
      showCommissionModal: false,
      showCommissionDeleteModal: false,
      commissionToDelete: null,
      commissionRates: [],
      commissionForm: {
        rate: '',
        start_date: '',
        end_date: '',
      },
      commissionFields: [
        { key: 'rate', label: 'Taux', class: 'text-center' },
        { key: 'period', label: 'Période' },
        { key: 'status', label: 'Statut', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      // Password modal data
      showPasswordModal: false,
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      showPassword: false,
      showConfirmPassword: false,
      isPasswordSubmitting: false,
    }
  },

  computed: {
    isAdmin() {
      const { user } = storeAuth.state
      return user && (user.role === 'admin' || user.role === 'superadmin')
    },

    // Password strength computation
    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
      )
    },

    isPasswordFormValid() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordsMatch
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.fetchStaffDetails()
  },

  methods: {
    async fetchStaffDetails() {
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/staff-members/${id}/`)
        this.staff = data
        this.isLoading = false
        if (this.staff.role === 'sales') {
          await this.loadCommissionRates()
        } else {
          this.commissionRates = []
        }
      } catch (error) {
        this.error = 'Erreur lors du chargement des données du membre du personnel'
        this.isLoading = false
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      }
    },

    getInitials(name) {
      if (!name) return 'U'
      return name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('')
    },

    formatRole(role) {
      const roleMap = {
        admin: 'Administrateur',
        superadmin: 'Super Administrateur',
        supplier: 'Fournisseur',
        delivery_man: 'Livreur',
        pickupeur: 'Ramasseur',
        manager: 'Gestionnaire',
        outsourcing_company: 'Société de sous-traitance',
        sales: 'Commercial',
        warehouse_operator: 'Magasinier',
        customer_support: 'Support Client',
        accountant: 'Comptable',
        admin_developer: 'Admin Développeur',
      }
      return roleMap[role] || role
    },

    formatDate(dateString) {
      if (!dateString) return 'Non définie'
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: fr })
    },

    formatShortDate(dateString) {
      if (!dateString) return 'Non définie'
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr })
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },

    getCommissionStatusVariant(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'secondary'
      if (rate.start_date <= today && rate.end_date >= today) return 'success'
      return 'info'
    },

    getCommissionStatusLabel(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'Terminé'
      if (rate.start_date <= today && rate.end_date >= today) return 'Actif'
      return 'À venir'
    },

    canDeleteCommission(rate) {
      const today = new Date().toISOString().split('T')[0]
      return rate.start_date > today
    },

    openCommissionModal() {
      this.resetCommissionForm()
      this.showCommissionModal = true
    },

    resetCommissionForm() {
      this.commissionForm = {
        rate: '',
        start_date: this.getTodayDateString(),
        end_date: this.getNextYearDateString(),
      }
    },

    getTodayDateString() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },

    getNextYearDateString() {
      const nextYear = new Date()
      nextYear.setFullYear(nextYear.getFullYear() + 1)
      return nextYear.toISOString().split('T')[0]
    },

    confirmDeleteCommission(rate) {
      this.commissionToDelete = rate
      this.showCommissionDeleteModal = true
    },

    async handleCommissionDelete() {
      if (!this.commissionToDelete) return
      await this.deleteCommissionRate(this.commissionToDelete)
      this.commissionToDelete = null
      this.showCommissionDeleteModal = false
    },

    async loadCommissionRates() {
      if (!this.staff?.id) return
      this.loadingCommissions = true
      try {
        const { data } = await axios.get('/api/commissions/commission-rates/', {
          params: { salesperson: this.staff.id },
        })
        this.commissionRates = data.results || data
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des commissions')
        this.commissionRates = []
      } finally {
        this.loadingCommissions = false
      }
    },

    async createCommissionRate() {
      if (this.creatingCommission) return
      this.creatingCommission = true
      try {
        await axios.post('/api/commissions/commission-rates/', {
          salesperson: this.staff.id,
          rate: this.commissionForm.rate,
          start_date: this.commissionForm.start_date,
          end_date: this.commissionForm.end_date,
        })
        this.showCommissionModal = false
        this.resetCommissionForm()
        this.showToast('success', 'Période de commission créée avec succès')
        this.loadCommissionRates()
      } catch (error) {
        const message = error.response?.data?.detail || error.response?.data?.error || 'Erreur lors de la création'
        this.showToast('danger', message)
      } finally {
        this.creatingCommission = false
      }
    },

    async deleteCommissionRate(rate) {
      try {
        await axios.delete(`/api/commissions/commission-rates/${rate.id}/`)
        this.showToast('success', 'Période supprimée')
        this.loadCommissionRates()
      } catch (error) {
        const message = error.response?.data?.error || 'Erreur lors de la suppression'
        this.showToast('danger', message)
      }
    },

    toggleStaffStatus() {
      this.statusModalTitle = this.staff.is_active ? 'Désactiver le membre' : 'Activer le membre'
      this.statusModalMessage = this.staff.is_active
        ? 'Êtes-vous sûr de vouloir désactiver ce membre du personnel ? Il ne pourra plus accéder à son compte.'
        : 'Êtes-vous sûr de vouloir activer ce membre du personnel ? Il pourra à nouveau accéder à son compte.'
      this.$refs.statusModal.show()
    },

    async handleStatusConfirmed() {
      try {
        await axios.put(`/api/auth/staff-members/${this.staff.id}/deactivate/`)
        this.staff.is_active = !this.staff.is_active
        this.showToast('success', `Le membre a été ${this.staff.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    // Password modal methods
    openPasswordModal() {
      this.showPasswordModal = true
    },

    resetPasswordForm() {
      this.passwordForm = {
        password: '',
        confirm_password: '',
      }
      this.showPassword = false
      this.showConfirmPassword = false
      this.isPasswordSubmitting = false
    },

    onPasswordGenerated(generatedPassword) {
      this.passwordForm.password = generatedPassword
      this.passwordForm.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return dateStr
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} à ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },

    async savePasswordChange() {
      if (!this.isPasswordFormValid) return

      this.isPasswordSubmitting = true
      try {
        await axios.post(
          `/api/auth/staff-members/${this.staff.id}/change-password/`,
          this.passwordForm,
        )

        const { user } = storeAuth.state
        const userDisplayName = user
          ? `${user.username} (${user.role || 'Utilisateur'})`
          : 'Utilisateur connecté'

        const newAuditLog = {
          id: Date.now(),
          changed_by_name: user ? user.username : 'Moi',
          changed_by_display: userDisplayName,
          created_at: new Date().toISOString(),
        }

        this.$set(this.staff, 'last_password_change', newAuditLog)
        if (!this.staff.password_change_history) {
          this.$set(this.staff, 'password_change_history', [])
        }
        this.staff.password_change_history.unshift(newAuditLog)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.showPasswordModal = false
        this.resetPasswordForm()
      } catch (error) {
        const errorMsg = error.response?.data?.password?.[0]
          || error.response?.data?.confirm_password?.[0]
          || error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors du changement de mot de passe'

        this.showToast('danger', errorMsg)
      } finally {
        this.isPasswordSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Modern Manager Detail Styling with Team Parcel Express Branding (TPE Blue)
.manager-detail-modern {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Skeleton Loading Styles
.skeleton-loading {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Base skeleton animation
@keyframes skeleton-pulse {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-element {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-pulse 1.5s infinite;
  border-radius: 8px;
}

// Skeleton Header
.skeleton-header {
  .skeleton-title {
    @extend .skeleton-element;
    width: 200px;
    height: 40px;
    border-radius: 8px;
  }

  .skeleton-subtitle {
    @extend .skeleton-element;
    width: 300px;
    height: 20px;
    border-radius: 6px;
  }

  .skeleton-actions {
    display: flex;
    gap: 0.5rem;

    .skeleton-btn {
      @extend .skeleton-element;
      width: 120px;
      height: 44px;
      border-radius: 12px;
    }
  }
}

// Skeleton Profile Card
.skeleton-profile-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .skeleton-profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .skeleton-avatar {
      @extend .skeleton-element;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      margin: 0 auto 1.5rem auto;
    }

    .skeleton-name {
      @extend .skeleton-element;
      width: 150px;
      height: 28px;
      margin: 0 auto 0.5rem auto;
      border-radius: 6px;
    }

    .skeleton-role {
      @extend .skeleton-element;
      width: 120px;
      height: 16px;
      margin: 0 auto 1rem auto;
      border-radius: 4px;
    }

    .skeleton-status {
      @extend .skeleton-element;
      width: 80px;
      height: 32px;
      margin: 0 auto;
      border-radius: 16px;
    }
  }

  .skeleton-profile-stats {
    padding: 1.5rem;

    .skeleton-stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .skeleton-stat-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-stat-content {
        flex: 1;

        .skeleton-stat-label {
          @extend .skeleton-element;
          width: 80px;
          height: 12px;
          margin-bottom: 0.5rem;
          border-radius: 4px;
        }

        .skeleton-stat-value {
          @extend .skeleton-element;
          width: 120px;
          height: 16px;
          border-radius: 4px;
        }
      }
    }
  }
}

// Skeleton Information Cards
.skeleton-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .skeleton-info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &.skeleton-info-card-full {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .skeleton-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .skeleton-card-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-card-title {
        @extend .skeleton-element;
        width: 180px;
        height: 20px;
        border-radius: 6px;
      }
    }

    .skeleton-card-body {
      padding: 1.5rem;

      .skeleton-info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .skeleton-info-icon {
          @extend .skeleton-element;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .skeleton-info-content {
          flex: 1;

          .skeleton-info-label {
            @extend .skeleton-element;
            width: 60px;
            height: 12px;
            margin-bottom: 0.5rem;
            border-radius: 4px;
          }

          .skeleton-info-value {
            @extend .skeleton-element;
            width: 150px;
            height: 16px;
            border-radius: 4px;

            &.skeleton-info-value-long {
              width: 250px;
            }
          }
        }
      }
    }
  }
}

// Page Header Styling
.page-header {
  .page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .page-subtitle {
    font-size: 1.1rem;
    color: #6c757d;
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-modern {
    background: linear-gradient(135deg, #0229c1 0%, #011fa3 100%);
    border: none;
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(2, 41, 193, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(2, 41, 193, 0.4);
    }

    &.btn-modern-outline {
      background: white;
      border: 2px solid #e9ecef;
      color: #6c757d;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      &:hover {
        border-color: #0229c1;
        background: #0229c1;
        color: white;
      }
    }
  }
}

// Modern Profile Card
.profile-card-modern {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .profile-avatar {
      position: relative;
      display: inline-block;
      margin-bottom: 1.5rem;

      .avatar-circle-large {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: linear-gradient(135deg, #0229c1 0%, #011fa3 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 10px 30px rgba(2, 41, 193, 0.3);

        .avatar-initials-large {
          color: white;
          font-weight: 700;
          font-size: 2rem;
        }
      }

      .status-indicator-large {
        position: absolute;
        bottom: 5px;
        right: 5px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 4px solid white;
        background: #0229c1;

        &.active {
          background: #16a34a;
        }
      }
    }

    .profile-name {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }

    .profile-role {
      color: #6c757d;
      font-size: 1rem;
      margin-bottom: 1rem;
    }

    .profile-status {
      .status-badge-large {
        padding: 0.5rem 1.5rem;
        border-radius: 25px;
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.active {
          background: rgba(22, 163, 74, 0.1);
          color: #16a34a;
        }

        &.inactive {
          background: rgba(2, 41, 193, 0.1);
          color: #0229c1;
        }
      }
    }
  }

  .profile-stats {
    padding: 1.5rem;

    .stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: rgba(2, 41, 193, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0229c1;
        margin-right: 1rem;
      }

      .stat-content {
        flex: 1;

        .stat-label {
          display: block;
          font-size: 0.8rem;
          color: #8b92b2;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          display: block;
          font-size: 1rem;
          color: #1f2937;
          font-weight: 600;
        }
      }
    }
  }
}

// Information Cards Grid
.info-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    }

    &:nth-child(3) {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .info-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .info-card-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;

        &.contact {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        &.work {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        &.personal {
          background: rgba(2, 41, 193, 0.1);
          color: #0229c1;
        }
      }

      .info-card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
      }
    }

    .info-card-body {
      padding: 1.5rem;

      .info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .info-icon {
          color: #8b92b2;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;

          .info-label {
            display: block;
            font-size: 0.8rem;
            color: #8b92b2;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.25rem;
          }

          .info-value {
            display: block;
            font-size: 1rem;
            color: #1f2937;
            font-weight: 500;
            word-break: break-word;
          }
        }
      }
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .manager-detail-modern {
    padding: 1rem;
  }

  .skeleton-loading {
    padding: 1rem;
  }

  .page-header {
    .page-title {
      font-size: 2rem;
    }

    .header-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .btn-modern {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .skeleton-header {
    .skeleton-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .skeleton-btn {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .info-cards-grid {
    grid-template-columns: 1fr;

    .info-card:nth-child(3) {
      grid-column: 1;
    }
  }

  .skeleton-info-grid {
    grid-template-columns: 1fr;

    .skeleton-info-card.skeleton-info-card-full {
      grid-column: 1;
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
